// frontend/src/pages/MyStatementPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MemberLayout from './MemberLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const MyStatementPage = () => {
  const navigate = useNavigate();
  const [memberId, setMemberId] = useState('');
  const [memberName, setMemberName] = useState('');
  const [startMonth, setStartMonth] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [includeShares, setIncludeShares] = useState(true);
  const [includeLoans, setIncludeLoans] = useState(true);
  const [statement, setStatement] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    initializeMemberData();
  }, []);

  const initializeMemberData = () => {
    const storedUser = localStorage.getItem('hopeSaccoUser');

    if (!storedUser) {
      navigate('/login');
      return;
    }

    const userData = JSON.parse(storedUser);

    // Allow member, admin, and official roles
    const allowedRoles = ['member', 'admin', 'official'];
    if (!allowedRoles.includes(userData.role)) {
      navigate('/login');
      return;
    }

    const userMemberId = userData.profile?.memberId;
    const userName = userData.profile?.displayName || userData.email;

    if (!userMemberId) {
      setError('Member ID not found');
      return;
    }

    setMemberId(userMemberId);
    setMemberName(userName);

    // Set default dates (last 6 months)
    const now = new Date();
    const endDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const startDate = new Date(now.getFullYear(), now.getMonth() - 5, 1);
    const startFormatted = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}`;

    setStartMonth(startFormatted);
    setEndMonth(endDate);
  };

  const handleGenerateStatement = async () => {
    if (!memberId || !startMonth || !endMonth) {
      setError('Please select date range');
      return;
    }

    setIsGenerating(true);
    setError('');
    setStatement(null);

    try {
      const token = localStorage.getItem('hopeSaccoToken');

      const params = new URLSearchParams({
        fromMonth: startMonth,
        toMonth: endMonth
      });

      const response = await fetch(`${API_URL}/reports/member-statement/${memberId}?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to generate statement');
      }

      const data = await response.json();

      if (data.success) {
        setStatement(data.data);
      } else {
        setError(data.message || 'Failed to generate statement');
      }
    } catch (err) {
      console.error('Error generating statement:', err);
      setError('Failed to generate statement');
    } finally {
      setIsGenerating(false);
    }
  };

  // convert statement month label to sortable YYYY-MM for filtering
  const monthLabelToKey = (label) => {
    if (!label) return null;

    // If already YYYY-MM, use it directly
    if (/^\d{4}-\d{2}$/.test(label)) return label;

    // Handles formats like "Aug-25", "Jan-26"
    const months = {
      Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
      Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12'
    };

    const parts = String(label).split('-');
    if (parts.length !== 2) return null;

    const mon = parts[0].trim();
    const yy = parts[1].trim();

    const mm = months[mon];
    if (!mm) return null;

    const yearNum = Number(yy);
    if (Number.isNaN(yearNum)) return null;

    const yyyy = 2000 + yearNum;
    return `${yyyy}-${mm}`;
  };

  const handleDownloadExcel = async () => {
    if (!statement) return;

    try {
      const token = localStorage.getItem('hopeSaccoToken');
      const params = new URLSearchParams({ fromMonth: startMonth, toMonth: endMonth });
      const url = `${API_URL}/reports/member-statement/${memberId}/excel?${params.toString()}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Server returned an error generating the Excel file');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `Statement_${memberId}_${startMonth}_to_${endMonth}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);

    } catch (err) {
      console.error('Error downloading Excel:', err);
      setError('Failed to download statement');
    }
  };

  return (
    <MemberLayout
      activePage="statement"
      pageTitle="My Statement"
      breadcrumbs={[
        { label: 'Dashboard', link: '/member/dashboard' },
        { label: 'My Statement' }
      ]}
    >
      <div className="page-content">
        {/* Generate Statement Form */}
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Generate Statement</h2>

          <div className="form-group">
            <label>Member</label>
            <input
              type="text"
              value={`${memberId} - ${memberName}`}
              disabled
              style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div className="form-group">
              <label>Start Month *</label>
              <input
                type="month"
                value={startMonth}
                onChange={(e) => setStartMonth(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label>End Month *</label>
              <input
                type="month"
                value={endMonth}
                onChange={(e) => setEndMonth(e.target.value)}
                required
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1.5rem', marginTop: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={includeShares}
                onChange={(e) => setIncludeShares(e.target.checked)}
              />
              <span>Include Shares</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={includeLoans}
                onChange={(e) => setIncludeLoans(e.target.checked)}
              />
              <span>Include Loans</span>
            </label>
          </div>

          {error && (
            <div className="alert alert-error" style={{ marginTop: '1rem' }}>
              {error}
            </div>
          )}

          <div style={{ marginTop: '1.5rem' }}>
            <button
              className="btn btn-primary"
              onClick={handleGenerateStatement}
              disabled={isGenerating || !startMonth || !endMonth}
            >
              {isGenerating ? '⏳ Generating...' : '📊 Generate Statement'}
            </button>
          </div>
        </div>

        {/* Display Statement */}
        {statement && (
          <div className="card" style={{ marginTop: '2rem' }}>
            {/* ✅ Styles only for this page/table */}
            <style>{`
              .mp-statement-table tbody tr:hover td {
                background: #dbeafe; /* light blue hover */
              }
              .mp-sticky-month {
                position: sticky;
                left: 0;
                z-index: 3;
                background: #ffffff;
              }
              .mp-sticky-month-header {
                position: sticky;
                left: 0;
                z-index: 5;
                background: #4c6ef5; /* match your header blue */
              }
            `}</style>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ margin: 0 }}>Statement Preview</h2>
              <button className="btn btn-success" onClick={handleDownloadExcel}>
                📥 Download Excel
              </button>
            </div>

            {/* Member Header */}
            <div style={{ marginBottom: '1rem', color: '#555' }}>
              <div><strong>Member:</strong> {(statement.member?.memberId || memberId)} - {(statement.member?.fullName || memberName)}</div>
              <div><strong>Period:</strong> {(statement.fromMonth || startMonth)} to {(statement.toMonth || endMonth)}</div>
            </div>

            {/* Totals Summary (UNCHANGED) */}
            <div style={{
              background: '#f8f9fa',
              padding: '1.5rem',
              borderRadius: '8px',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{ marginTop: 0 }}>Totals (Monthly Deductions + Interest)</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div>
                  <div style={{ color: '#666', fontSize: '0.875rem' }}>Total Shares Contributed</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#28a745' }}>
                    KES {(statement.summary?.totalSharesContributed || 0).toLocaleString()}
                  </div>
                </div>

                <div>
                  <div style={{ color: '#666', fontSize: '0.875rem' }}>Total Loan Interest</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                    KES {(statement.summary?.totalLoanInterest || 0).toLocaleString()}
                  </div>
                </div>

                <div>
                  <div style={{ color: '#666', fontSize: '0.875rem' }}>Total Loan Repayments</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                    KES {(statement.summary?.totalLoanRepayments || 0).toLocaleString()}
                  </div>
                </div>

                <div>
                  <div style={{ color: '#666', fontSize: '0.875rem' }}>Total Deductions</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#dc3545' }}>
                    KES {(statement.summary?.grandTotalDeductions || 0).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>

            {/* Statement Table */}
            {Array.isArray(statement.statement) && statement.statement.length > 0 ? (
              <div className="table-responsive" style={{ overflowX: 'auto', width: '100%' }}>
                <table className="data-table mp-statement-table" style={{ minWidth: '1600px' }}>
                  <thead>
                    <tr>
                      <th className="mp-sticky-month-header">Month</th>

                      {includeShares && (
                        <>
                          <th className="text-right">Shares Bal</th>
                          <th className="text-right">Shares Contrib</th>
                        </>
                      )}

                      {includeLoans && (
                        <>
                          <th className="text-right">Loan Bal</th>
                          <th className="text-right">Loan Int</th>
                          <th className="text-right">Loan Repay</th>
                          <th className="text-right">Add Normal</th>

                          <th className="text-right">School Bal</th>
                          <th className="text-right">School Int</th>
                          <th className="text-right">School Repay</th>
                          <th className="text-right">Add School</th>

                          <th className="text-right">Emerg Bal</th>
                          <th className="text-right">Emerg Int</th>
                          <th className="text-right">Emerg Repay</th>
                          <th className="text-right">Add Emerg</th>
                        </>
                      )}

                      <th className="text-right">Appl Fees</th>
                      <th className="text-right">Total Deductions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {statement.statement.map((r, idx) => (
                      <tr key={idx}>
                        <td className="mp-sticky-month">{r.month}</td>

                        {includeShares && (
                          <>
                            <td className="text-right">{(r.shares || 0).toLocaleString()}</td>
                            <td className="text-right" style={{ color: '#28a745' }}>
                              {(r.sharesContribution || 0).toLocaleString()}
                            </td>
                          </>
                        )}

                        {includeLoans && (
                          <>
                            <td className="text-right">{(r.loanBalance || 0).toLocaleString()}</td>
                            <td className="text-right">{(r.loanInterest || 0).toLocaleString()}</td>
                            <td className="text-right">{(r.loanRepayment || 0).toLocaleString()}</td>
                            <td className="text-right">{(r.additionalNormalDisbursed || 0).toLocaleString()}</td>

                            <td className="text-right">{(r.schoolFeesLoan || 0).toLocaleString()}</td>
                            <td className="text-right">{(r.schoolFeesInterest || 0).toLocaleString()}</td>
                            <td className="text-right">{(r.schoolFeesRepayment || 0).toLocaleString()}</td>
                            <td className="text-right">{(r.additionalSchoolDisbursed || 0).toLocaleString()}</td>

                            <td className="text-right">{(r.emergencyLoan || 0).toLocaleString()}</td>
                            <td className="text-right">{(r.emergencyLoanInterest || 0).toLocaleString()}</td>
                            <td className="text-right">{(r.emergencyLoanRepayment || 0).toLocaleString()}</td>
                            <td className="text-right">{(r.additionalEmergencyDisbursed || 0).toLocaleString()}</td>
                          </>
                        )}

                        <td className="text-right">{(r.applicationFees || 0).toLocaleString()}</td>
                        <td className="text-right" style={{ color: '#dc3545', fontWeight: 'bold' }}>
                          {(r.totalDeductions || 0).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="alert alert-info">
                No statement rows found for the selected period.
              </div>
            )}
          </div>
        )}
      </div>
    </MemberLayout>
  );
};

export default MyStatementPage;
